// src/components/projects/ProjectMap.jsx
// ✅ Mappa cantieri con Leaflet (OpenStreetMap — gratuito, no API key)
// ✅ Bottone GPS per rilevare posizione automatica
import React, { useEffect, useRef, useState } from 'react';
import { MapPinIcon, ArrowPathIcon, SignalIcon } from '@heroicons/react/24/outline';

// Carica Leaflet dinamicamente (evita SSR issues)
const loadLeaflet = () => new Promise((resolve) => {
    if (window.L) { resolve(window.L); return; }

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    document.head.appendChild(link);

    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.onload = () => resolve(window.L);
    document.head.appendChild(script);
});

// ==================== MAPPA PROGETTI ====================
export const ProjectsMap = ({ projects }) => {
    const mapRef = useRef(null);
    const mapInstance = useRef(null);
    const [loading, setLoading] = useState(true);

    const geoProjects = projects.filter(p => p.latitude && p.longitude);

    useEffect(() => {
        if (!mapRef.current || geoProjects.length === 0) { setLoading(false); return; }

        loadLeaflet().then(L => {
            // Distruggi mappa precedente se esiste
            if (mapInstance.current) { mapInstance.current.remove(); mapInstance.current = null; }

            const map = L.map(mapRef.current, { zoomControl: true, scrollWheelZoom: false });
            mapInstance.current = map;

            L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                attribution: '© OpenStreetMap contributors',
                maxZoom: 19,
            }).addTo(map);

            // Colori per status
            const statusColors = {
                IN_PROGRESS: '#10b981', PLANNED: '#3b82f6', DRAFT: '#94a3b8',
                DELAYED: '#f59e0b', ON_HOLD: '#ef4444', COMPLETED: '#6b7280',
            };

            const markers = [];
            geoProjects.forEach(p => {
                const color = statusColors[p.status] || '#6b7280';
                const icon = L.divIcon({
                    className: '',
                    html: `<div style="
                        width:32px;height:32px;border-radius:50% 50% 50% 0;
                        background:${color};border:3px solid white;
                        box-shadow:0 2px 8px rgba(0,0,0,0.3);
                        transform:rotate(-45deg);
                        display:flex;align-items:center;justify-content:center;
                    "><div style="transform:rotate(45deg);color:white;font-size:10px;font-weight:700">
                        ${p.progress || 0}%
                    </div></div>`,
                    iconSize: [32, 32],
                    iconAnchor: [16, 32],
                    popupAnchor: [0, -36],
                });

                const pct = p.estimatedBudget > 0
                    ? ((p.actualCost || 0) / p.estimatedBudget * 100).toFixed(0) : 0;

                const marker = L.marker([p.latitude, p.longitude], { icon })
                    .bindPopup(`
                        <div style="min-width:180px;font-family:sans-serif">
                            <p style="font-weight:700;font-size:13px;margin:0 0 4px">${p.name}</p>
                            <p style="color:#64748b;font-size:11px;margin:0 0 6px">${p.location}</p>
                            <div style="display:flex;gap:6px;flex-wrap:wrap">
                                <span style="background:${color}20;color:${color};padding:2px 8px;border-radius:99px;font-size:10px;font-weight:600">
                                    ${p.status}
                                </span>
                                <span style="background:#f1f5f9;color:#475569;padding:2px 8px;border-radius:99px;font-size:10px">
                                    Budget: ${pct}%
                                </span>
                            </div>
                        </div>
                    `, { maxWidth: 220 })
                    .addTo(map);
                markers.push(marker);
            });

            // Centra la mappa sui marker
            if (markers.length > 0) {
                const group = L.featureGroup(markers);
                map.fitBounds(group.getBounds().pad(0.2));
            }

            setLoading(false);
        });

        return () => { if (mapInstance.current) { mapInstance.current.remove(); mapInstance.current = null; } };
    }, [geoProjects.length]);

    if (geoProjects.length === 0) {
        return (
            <div className="bg-white rounded-2xl border p-8 text-center">
                <MapPinIcon className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="text-sm font-medium text-gray-500">Nessun cantiere geolocalizzato</p>
                <p className="text-xs text-gray-400 mt-1">Aggiungi le coordinate GPS nei dettagli del progetto</p>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-2xl border overflow-hidden">
            <div className="px-4 py-3 border-b flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <MapPinIcon className="w-4 h-4 text-blue-500" />
                    <h3 className="text-sm font-semibold text-gray-700">Mappa Cantieri</h3>
                    <span className="text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full font-medium">
                        {geoProjects.length} geolocalizzati
                    </span>
                </div>
                <div className="flex gap-3 text-xs text-gray-400">
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" />In corso</span>
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500 inline-block" />Pianificato</span>
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-yellow-500 inline-block" />In ritardo</span>
                </div>
            </div>
            {loading && (
                <div className="h-64 flex items-center justify-center">
                    <ArrowPathIcon className="w-6 h-6 text-blue-500 animate-spin" />
                </div>
            )}
            <div ref={mapRef} style={{ height: '380px', display: loading ? 'none' : 'block' }} />
        </div>
    );
};

// ==================== BOTTONE GPS (per ProjectForm) ====================
export const GPSButton = ({ onLocationDetected, loading: externalLoading }) => {
    const [detecting, setDetecting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    const detectGPS = () => {
        if (!navigator.geolocation) {
            setError('GPS non supportato dal browser');
            return;
        }
        setDetecting(true);
        setError(null);
        setSuccess(false);

        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const { latitude, longitude, accuracy } = pos.coords;
                onLocationDetected({ latitude, longitude, accuracy });
                setDetecting(false);
                setSuccess(true);
                setTimeout(() => setSuccess(false), 3000);
            },
            (err) => {
                setDetecting(false);
                const msgs = {
                    1: 'Permesso GPS negato. Abilita la geolocalizzazione.',
                    2: 'GPS non disponibile. Riprova.',
                    3: 'Timeout GPS. Riprova.',
                };
                setError(msgs[err.code] || 'Errore GPS');
            },
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
        );
    };

    return (
        <div className="space-y-1.5">
            <button type="button" onClick={detectGPS}
                disabled={detecting || externalLoading}
                className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition ${success ? 'bg-emerald-100 text-emerald-700' :
                        'bg-blue-50 text-blue-600 hover:bg-blue-100'
                    } disabled:opacity-50`}>
                {detecting
                    ? <ArrowPathIcon className="w-4 h-4 animate-spin" />
                    : <SignalIcon className="w-4 h-4" />
                }
                {detecting ? 'Rilevamento GPS...' : success ? '✅ Posizione rilevata' : '📍 Rileva posizione GPS'}
            </button>
            {error && <p className="text-xs text-red-500">{error}</p>}
        </div>
    );
};

export default ProjectsMap;